import { useMemo } from 'react';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { usePlanOverageUsageData } from './usePlanOverageUsageData';
import { OveragesTooltip } from 'assets/js/pages/account-settings/account-about/givebutter-plus/OveragesTooltip';
import { useSubscriptionPlans } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useSubscriptionPlans';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { getCostDifferenceBetweenPlans } from 'customHooks/useRecommendedPlanComparison';
import { LONG_DATE_FORMAT, formatDate, isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { twJoin, twMerge } from 'tailwind-merge';
import { GivebutterPlusPlan } from 'types/domain';

interface SubscriptionPlanLineItemsProps {
    plan: GivebutterPlusPlan;
    showAnnualPricing: boolean;
}
const SubscriptionPlanLineItems = ({ plan, showAnnualPricing }: SubscriptionPlanLineItemsProps) => {
    const { validGivebutterPlusPlan } = useCurrentAccount();
    const { data: plans } = useSubscriptionPlans();
    const { isGracePeriod } = usePlanOverageUsageData();

    const dateToReportOverages = useMemo(() => {
        if (validGivebutterPlusPlan?.overage_reporting_starting_at) {
            return isoDateFromServer(validGivebutterPlusPlan.overage_reporting_starting_at);
        }
        return DateTime.now().plus({ days: 30 });
    }, [validGivebutterPlusPlan]);

    const gracePeriodPricePerMonth = showAnnualPricing
        ? plan.annualized_monthly_price
        : plan.monthly_price;

    const billingCyclePrices = showAnnualPricing
        ? plan.billing.yearly_billing_cycle
        : plan.billing.monthly_billing_cycle;
    const standardPricePerMonth = billingCyclePrices.perMonth;

    const overagesWontApply = isGracePeriod || !validGivebutterPlusPlan;
    const totalPriceDue = overagesWontApply ? gracePeriodPricePerMonth : standardPricePerMonth;

    const savingsWithNewPlan = useMemo(() => {
        if (!validGivebutterPlusPlan) {
            return 0;
        }
        const currentPlan = plans.find(
            plan => plan.contacts_max === validGivebutterPlusPlan.tier.max_contacts,
        );
        if (!currentPlan) {
            return 0;
        }

        const currentlyOnAnnualPlan = validGivebutterPlusPlan.tier.interval === 'year';
        const currentBillingCycle = currentlyOnAnnualPlan
            ? currentPlan.billing.yearly_billing_cycle
            : currentPlan.billing.monthly_billing_cycle;

        return getCostDifferenceBetweenPlans(
            currentBillingCycle,
            billingCyclePrices,
            showAnnualPricing,
        );
    }, [validGivebutterPlusPlan, plans, billingCyclePrices, showAnnualPricing]);

    return (
        <div className="flex flex-col gap-2">
            <div className="flex justify-between gap-8 text-nowrap text-sm text-gray-600">
                <span className="shrink-0">
                    Up to {maskWholeNumber(plan.contacts_max)} contacts
                </span>
                <span>
                    <CurrencySymbol />
                    {maskDecimal(plan.monthly_price)}
                </span>
            </div>
            {!!plan.overage_pack_count && (
                <div className="flex justify-between gap-8 text-nowrap text-sm text-gray-600">
                    <span className="flex shrink-0 gap-2">
                        Extra 100 contacts &times; {plan.overage_pack_count}
                        <OveragesTooltip />
                    </span>
                    <div className="flex gap-2">
                        <span className={twJoin(overagesWontApply && 'line-through')}>
                            <CurrencySymbol />
                            {maskDecimal(plan.extra_contacts_due_price_monthly)}
                        </span>
                        {overagesWontApply && (
                            <span>
                                <CurrencySymbol />
                                {'0.00'}
                            </span>
                        )}
                    </div>
                </div>
            )}
            <div
                className={twMerge(
                    'text-brand-violet-primary invisible flex justify-between gap-1 text-nowrap text-sm font-medium',
                    showAnnualPricing && 'visible',
                )}
            >
                <span className="shrink-0">Monthly discount</span>
                <span>
                    &minus;
                    <CurrencySymbol />
                    {maskDecimal(
                        Math.round(
                            Math.abs(plan.annualized_monthly_price - plan.monthly_price) * 100,
                        ) / 100,
                    )}
                </span>
            </div>
            {overagesWontApply && (
                <div className="rounded-md bg-gray-50 p-4 text-sm">
                    <p className="font-medium text-gray-700">No overages for your first month.</p>
                    <p className="text-gray-600">
                        To support you in your data migration, Givebutter will waive all overage
                        charges until{' '}
                        <span className="font-medium">
                            {formatDate(dateToReportOverages, LONG_DATE_FORMAT)}.
                        </span>
                    </p>
                </div>
            )}
            <Separator />
            <div className="flex justify-between gap-1 text-nowrap">
                <div className="text-sm font-medium text-gray-600">Total</div>
                <div className="text-right">
                    <div className="font-medium text-gray-900">
                        <span className="text-2xl">
                            <CurrencySymbol />
                            {maskDecimal(totalPriceDue)}
                        </span>
                        &nbsp;
                        <span className="text-lg">/mo</span>
                    </div>

                    {!!plan.overage_pack_count && overagesWontApply && (
                        <span className="text-xs text-gray-600">
                            for your first month, then <CurrencySymbol />
                            {maskDecimal(standardPricePerMonth)}/mo
                        </span>
                    )}

                    <div
                        className={twMerge(
                            'invisible text-xs text-gray-600',
                            showAnnualPricing && 'visible',
                        )}
                    >
                        <CurrencySymbol />
                        {maskDecimal(plan.total_yearly)} Billed yearly
                    </div>
                    {savingsWithNewPlan > 0 && (
                        <p className="text-success-500 text-sm font-medium">
                            You’ll save <CurrencySymbol />
                            {maskDecimal(savingsWithNewPlan)} {showAnnualPricing ? '/year' : '/mo'}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export { SubscriptionPlanLineItems };
